/**
 * Memoized selectors for bids
 * Uses createSelector for performance optimization
 */

import { createSelector } from '@reduxjs/toolkit';

// Base selectors
export const selectBidsState = state => state.bids || {};
export const selectAllBids = createSelector(
  [selectBidsState],
  bidsState => bidsState.allBids || []
);
export const selectUserBids = createSelector(
  [selectBidsState],
  bidsState => bidsState.userBids || []
);
export const selectCrewBidsMap = createSelector(
  [selectBidsState],
  bidsState => bidsState.crewBids || {}
);
export const selectBidsLoading = createSelector(
  [selectBidsState],
  bidsState => bidsState.fetchAllBidsInProgress || false
);
export const selectBidsError = createSelector(
  [selectBidsState],
  bidsState => bidsState.fetchAllBidsError || null
);
export const selectBidsMetadata = createSelector([selectBidsState], bidsState => ({
  lastFetchTime: bidsState.lastFetchTime || null,
  fetchUserBidsInProgress: bidsState.fetchUserBidsInProgress || false,
  fetchUserBidsError: bidsState.fetchUserBidsError || null,
  fetchCrewBidsInProgress: bidsState.fetchCrewBidsInProgress || false,
  fetchCrewBidsError: bidsState.fetchCrewBidsError || null,
}));

const filterBySearch = (bids, query) => {
  if (!query) return bids;

  const searchTerm = query.toLowerCase();
  return bids.filter(bid => {
    const title = bid.title?.toLowerCase() || '';
    const venue = bid.venue?.toLowerCase() || '';
    const location = bid.location?.toLowerCase() || '';
    const description = bid.description?.toLowerCase() || '';
    const eventType = bid.eventType?.toLowerCase() || '';

    return (
      title.includes(searchTerm) ||
      venue.includes(searchTerm) ||
      location.includes(searchTerm) ||
      description.includes(searchTerm) ||
      eventType.includes(searchTerm)
    );
  });
};

const filterByDateFrom = (bids, dateFrom) => {
  if (!dateFrom) return bids;

  const fromDate = new Date(dateFrom);
  return bids.filter(bid => new Date(bid.date) >= fromDate);
};

const filterByDateTo = (bids, dateTo) => {
  if (!dateTo) return bids;

  const toDate = new Date(dateTo);
  return bids.filter(bid => new Date(bid.date) <= toDate);
};

const filterByLocation = (bids, locationQuery) => {
  if (!locationQuery) return bids;

  const query = locationQuery.toLowerCase();
  return bids.filter(
    bid => bid.location?.toLowerCase().includes(query) || bid.venue?.toLowerCase().includes(query)
  );
};

const filterByMinPrice = (bids, minPrice) => {
  if (!minPrice) return bids;

  const min = parseFloat(minPrice);
  return bids.filter(bid => {
    const bidMin = Number.isFinite(parseFloat(bid.currentBid)) ? parseFloat(bid.currentBid) : null;
    const bidMax = Number.isFinite(parseFloat(bid.maxBid)) ? parseFloat(bid.maxBid) : bidMin;
    const effectiveUpperBound = bidMax != null ? bidMax : bidMin;

    return effectiveUpperBound == null || effectiveUpperBound >= min;
  });
};

const filterByMaxPrice = (bids, maxPrice) => {
  if (!maxPrice) return bids;

  const max = parseFloat(maxPrice);
  return bids.filter(bid => {
    const bidMin = Number.isFinite(parseFloat(bid.currentBid)) ? parseFloat(bid.currentBid) : null;
    const bidMax = Number.isFinite(parseFloat(bid.maxBid)) ? parseFloat(bid.maxBid) : bidMin;
    const effectiveLowerBound = bidMin != null ? bidMin : bidMax;

    return effectiveLowerBound == null || effectiveLowerBound <= max;
  });
};

const hasAppliedFilters = filters => Object.keys(filters || {}).some(key => Boolean(filters[key]));

export const selectActiveBids = createSelector([selectAllBids], bids =>
  bids.filter(bid => bid.status === 'active')
);

export const selectFeaturedActiveBids = createSelector([selectActiveBids], activeBids =>
  activeBids.slice(0, 3)
);

export const filterBidsByCriteria = (bids, searchQuery = '', appliedFilters = {}) => {
  const trimmedQuery = searchQuery.trim();

  if (!trimmedQuery && !hasAppliedFilters(appliedFilters)) {
    return null;
  }

  let pipeline = [...bids];
  pipeline = filterBySearch(pipeline, trimmedQuery);
  pipeline = filterByDateFrom(pipeline, appliedFilters.dateFrom);
  pipeline = filterByDateTo(pipeline, appliedFilters.dateTo);
  pipeline = filterByLocation(pipeline, appliedFilters.location);
  pipeline = filterByMinPrice(pipeline, appliedFilters.minPrice);
  pipeline = filterByMaxPrice(pipeline, appliedFilters.maxPrice);

  return pipeline;
};

export const getActiveFilteredBids = (bids, searchQuery = '', appliedFilters = {}) => {
  const filteredBids = filterBidsByCriteria(bids, searchQuery, appliedFilters);
  return filteredBids ? filteredBids.filter(bid => bid.status === 'active') : null;
};
